// pages/CreateAdmin.tsx — Super Admin only: create new admins
import { useState, useEffect } from 'react';
import { Eye, EyeOff, Shield, Copy, CheckCircle, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast';
import { copyText } from '../../shared/clipboard';

interface FormData { name: string; login_id?: string; password: string; wallet_balance: number; }

export default function CreateAdmin() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdLoginId, setCreatedLoginId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const { token, refreshCsrf, error, setError } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: { wallet_balance: 0 },
  });

  // Check if current user is super admin
  useEffect(() => {
    if (!token) return;
    try {
      const info = localStorage.getItem('adminInfo');
      if (info) {
        const parsed = JSON.parse(info);
        setIsSuperAdmin(!!parsed.is_super_admin);
      } else {
        // Decode JWT to check
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsSuperAdmin(!!payload.is_super_admin);
      }
    } catch { setIsSuperAdmin(false); }
    refreshCsrf();
  }, [token, refreshCsrf]);

  const copyToClipboard = async (text: string) => {
    try {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed. Select and copy manually.');
    }
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true); setError(''); setCreatedLoginId(null);
    try {
      const csrfToken = await refreshCsrf();
      const res = await fetch('/admin/create-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name.trim(),
          login_id: data.login_id?.trim() || undefined,
          password: data.password,
          wallet_balance: Number(data.wallet_balance || 0),
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error?.message || 'Failed to create admin');
      const loginId = result.login_id;
      setCreatedLoginId(loginId);
      toast.success(`Admin created! Login ID: ${loginId}`);
      reset();
    } catch (err: any) {
      setError(err.message); toast.error(err.message);
    } finally { setLoading(false); }
  };

  // Not super admin — show locked state
  if (isSuperAdmin === false) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-8 text-center">
          <Lock size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700 mb-2">Access Restricted</h2>
          <p className="text-red-600">Only Super Admins (SADMIN001 / SADMIN002) can create new admins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-100 p-3 rounded-full"><Shield size={28} className="text-purple-600" /></div>
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Create New Admin</h2>
          <p className="text-sm text-purple-600 font-medium">🔒 Super Admin Exclusive</p>
        </div>
      </div>

      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
        <strong>Note:</strong> New admins created here will <strong>not</strong> be super admins. Only SADMIN001 and SADMIN002 have super admin powers. New admins can manage users, managers, credits, and RTP — but cannot create other admins.
      </div>

      {createdLoginId && (
        <div className="mb-6 p-5 bg-green-50 border-2 border-green-400 rounded-xl">
          <p className="text-green-800 font-bold text-lg mb-2">✅ Admin Created Successfully!</p>
          <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-green-300">
            <div>
              <p className="text-xs text-gray-500 mb-1">Admin Login ID</p>
              <p className="text-2xl font-mono font-bold text-purple-700 tracking-widest">{createdLoginId}</p>
            </div>
            <button onClick={() => copyToClipboard(createdLoginId)}
              className="ml-auto p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition">
              {copied ? <CheckCircle size={20} className="text-green-600" /> : <Copy size={20} className="text-purple-600" />}
            </button>
          </div>
          <p className="mt-2 text-sm text-green-700">Share this Login ID and password securely with the new admin.</p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between">
          <span>{error}</span><button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Admin Full Name"
              {...register('name', { required: 'Name is required', pattern: { value: /^[A-Za-z\s]{2,50}$/, message: '2-50 letters only' } })} />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User ID (Optional)</label>
            <input className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Leave blank to auto-generate"
              {...register('login_id', { pattern: { value: /^[A-Za-z0-9_-]{3,20}$/, message: '3-20 letters, numbers, _ or -' } })} />
            {errors.login_id && <p className="mt-1 text-sm text-red-600">{errors.login_id.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input type={showPassword ? 'text' : 'password'}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 pr-12"
              placeholder="At least 8 characters"
              {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })} />
            <button type="button" className="absolute right-3 top-10 text-gray-600" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Admin Balance</label>
            <input type="number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Credits available for this admin to distribute"
              {...register('wallet_balance', {
                valueAsNumber: true,
                min: { value: 0, message: 'Balance cannot be negative' },
              })} />
            {errors.wallet_balance && <p className="mt-1 text-sm text-red-600">{errors.wallet_balance.message}</p>}
          </div>

          <button type="submit" disabled={loading}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
            {loading ? 'Creating...' : 'Create Admin'}
          </button>
        </form>
      </div>
      <p className="mt-4 text-xs text-gray-400 text-center">Enter a User ID to use it as Login ID, or leave blank to auto-generate ADM######.</p>
    </div>
  );
}
